<template>
    <div class="setup-organization-container">
        <!-- Step Label and Title -->
        <div class="step-info mb-2">
            <small class="step-label">STEP {{ currentStep }} OF 3</small>
            <h2 class="section-title">Setup Your Organization</h2>
        </div>

        <!-- Form Card -->
        <div class="form-card">
            <div class="form-group mb-4">
                <label for="companyName" class="form-label">Your Company Name</label>
                <input v-model="companyName" type="text" id="companyName" class="form-control" />
            </div>

            <h6 class="proofing-label">Proofing Defaults</h6>
            <div v-for="(setting, key) in proofingDefaults" :key="key" class="proofing-item ashore-toggle">
                <label class="switch">
                    <input type="checkbox" :id="key" v-model="setting.value" />
                    <span class="slider round"></span>
                </label>
                <div class="d-flex flex-shrink-0 proofing-label-container">
                    <label :for="key" class="proofing-text">
                        {{ setting.label }}
                    </label>
                    <img :id="`${key}_tooltip_icon`" src="/img/info.svg" alt="info-image" class="ml-1 info-icon" />
                    <b-tooltip :target="`${key}_tooltip_icon`" triggers="hover">
                        {{ setting.tooltip }}
                    </b-tooltip>
                </div>
            </div>
        </div>

        <!-- Buttons -->
        <div class="form-buttons">
            <button @click="handleContinueClick" class="btn btn-success fw-normal px-4 py-12 continue-button">Continue</button>
            <button @click="handlePrevStep" class="btn btn-light fw-normal text-gray-700 previous-button">Previous</button>
        </div>
    </div>
</template>

<script>

import { ALERT_TYPE } from '../../utils/constants';

const { WARNING, SUCCESS } = ALERT_TYPE;

const defaultNotificationSettings = (userSettings) => ({
    notifyFilesSent: true,
    notifyFilesApproved: true,
    notifyNewComments: true,
    notifyMeNewComments: 1,
    notifyMyApproversNewComments: 1,
    reminderDays: "Sun,Mon,Tue,Wed,Tr,Fri,Sat",
    reminderTimeOfDay: "09:00",
});


export default {
    name: "Screen2",
    props: {
        currentStep: {
            type: Number,
            required: true
        },
        handleNextStep: {
            type: Function,
            required: true
        },
        handlePrevStep: {
            type: Function,
            required: true
        },
    },
    data() {
        const userSettings = _.get(this, '$A.Auth.userSettings', {});
        const onboardingStep = _.get(this, '$A.Auth.onboardingStep', 1);
        const hasCompletedStep2 = onboardingStep > 2;

        return {
            companyName: _.get(this, '$A.Auth.account.name', ''),
            proofingDefaults: {
                viewAllComments: {
                    label: 'Enable real-time collaboration',
                    value: hasCompletedStep2 ? userSettings.viewAllComments : true,
                    tooltip: 'Turn this toggle on to allow your approvers and commenters to see, interact with, and be notified about other comments on a proof.'
                },
                blockCommentingAfterProofDecision: {
                    label: 'Disallow comments after an approver makes a decision',
                    value: hasCompletedStep2 ? userSettings.blockCommentingAfterProofDecision : true,
                    tooltip: 'Turn this toggle on to prevent approvers from submitting comments after making a decision.'
                },
                downloadFiles: {
                    label: 'Allow approvers to download files',
                    value: hasCompletedStep2 ? userSettings.downloadFiles : true,
                    tooltip: 'Turn this toggle on to allow approvers to download the files in your proof.'
                },
                approveIndividualFiles: {
                    label: 'Require approvers to approve each file individually',
                    value: hasCompletedStep2 ? userSettings.approveIndividualFiles : true,
                    tooltip: "Turn this toggle on to require approvers to make an approval decision on each file in your proof. Otherwise, we'll ask for one approval decision for all files in this proof."
                },
                approveWithChanges: {
                    label: 'Allow approvers to approve files with changes',
                    value: hasCompletedStep2 ? userSettings.approveWithChanges : true,
                    tooltip: "Turn this toggle on to allow your approvers to say, 'Make these changes, but I don't need to see a new version.' The proof will be treated as approved."
                },
                limitVersionVisibility: {
                    label: 'Hide internal versions of files from customers',
                    value: hasCompletedStep2 ? userSettings.limitVersionVisibility : true,
                    tooltip: 'Turn this toggle on to prevent approvers from viewing proof versions sent to approvers in other workflow stages.'
                },
            },
        };
    },
    mounted() {
        this.setCompanyName();
    },
    methods: {
        setCompanyName() {
            this.companyName = _.get(this, '$A.Auth.account.name', '');
        },
        async updateCompanyName() {
            if (!this.companyName || this.companyName.trim() === '') {
                this.$A.AlertUser("Company name cannot be empty", WARNING);
                return false;
            }

            try {
                const account = await this.$A.AccountService.UpdateAccount(
                    this.companyName,
                );

                if (account) {
                    this.$A.Auth.account = account;
                    this.$A.SaveSessionData(this.$A.Auth);
                    this.$A.AlertUser("Company name updated successfully", SUCCESS);
                    return true;
                }
                return false;
            } catch (error) {
                this.$A.AlertUser("Failed to update company name", WARNING);
                return false;
            }
        },

        async handleContinueClick() {
            const companyUpdated = await this.updateCompanyName();
            if (!companyUpdated) return;

            try {
                const userSettings = _.get(this, '$A.Auth.userSettings', {});
                const notificationSettings = defaultNotificationSettings(userSettings);
                const user = await this.$A.UserService.UpdateUserDefaultSettings(
                    this.$A.Auth.id,
                    this.proofingDefaults.downloadFiles.value,
                    this.proofingDefaults.approveIndividualFiles.value,
                    this.proofingDefaults.viewAllComments.value,
                    this.proofingDefaults.approveWithChanges.value,
                    notificationSettings.notifyFilesSent,
                    notificationSettings.notifyFilesApproved,
                    notificationSettings.notifyNewComments,
                    notificationSettings.notifyMeNewComments,
                    notificationSettings.notifyMyApproversNewComments,
                    this.proofingDefaults.limitVersionVisibility.value,
                    this.proofingDefaults.blockCommentingAfterProofDecision.value,
                    notificationSettings.reminderDays,
                    notificationSettings.reminderTimeOfDay,
                    _.get(this, '$A.Auth.userSettings.reminderTimeZone', 'UTC')
                );

                if (user.id !== undefined && user.id > 0) {
                    this.$A.UpdateUserObjectAndBustCache(user);
                    this.handleNextStep();
                } else {
                    this.$A.AlertUser("Failed to update proofing settings", WARNING);
                }
            } catch (error) {
                this.$A.AlertUser("Failed to update proofing settings", WARNING);
            }
        }
    }
};
</script>

<style scoped>

.setup-organization-container {
    margin-top: 60px;
}

.ashore-toggle .switch {
    margin-right: 0px;
}

@media (min-width: 1642px) {
    .setup-organization-container {
        margin-top: 106px;
    }
}

.step-info {
    text-align: left;
}

.step-label {
    font-size: 14px;
    font-weight: 700;
    color: #1B1B1C;
    text-transform: uppercase;
    line-height: 20px;
}

.section-title {
    font-size: 36px;
    line-height: 40px;
    font-weight: 800;
    color: #1B1B1C;
    margin-top: 4px;
    margin-bottom: 16px;
    font-family: 'Montserrat', sans-serif;
}

.form-card {
    background: #fff;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #575759;
    margin-bottom: 8px;
    opacity: 1;
}

.form-control {
    border: 1px solid #B5B5B8;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
}

.proofing-label {
    font-weight: 600;
    font-size: 16px;
    line-height: 28px;
    color: #3E3E40;
    margin: 16px 0 10px;
}

.proofing-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 6px;
    gap: 8px;
}

.proofing-label-container {
    align-items: flex-start;
    display: inline;
    flex: 1;
}

.proofing-text {
    font-size: 14px;
    line-height: 20px;
    color: #252526;
    opacity: 1;
    font-weight: 400;
    display: inline;
}

.info-icon {
    cursor: pointer;
    display: inline-block;
    vertical-align: middle;
    margin-left: 4px;
}

.form-buttons {
    margin-top: 24px;
    display: flex;
    gap: 16px;
    font-size: 16px;
    line-height: 24px;
}

.continue-button {
    font-weight: 400;
    padding: 13px 25px;
    color: #f9f9f9;
    font-size: 16px;
}

.previous-button {
    font-weight: 400;
    padding: 13px 25px;
    border: 1px solid #b5b5b8;
    background-color: white;
    font-size: 16px;
}

.ashore-toggle input:checked + .slider {
    background-color: #006F41;
}

@media (max-width: 576px) {
    .proofing-item {
        align-items: flex-start;
        gap: 12px;
    }

    .proofing-label-container {
        width: 100%;
    }

    .form-card {
        padding: 16px;
    }

    .section-title {
        font-size: 28px;
        line-height: 32px;
    }
}

</style>
